// src/modals/RecommendModal.jsx
// Phase 37 Plan 06 — extracted from src/App.jsx (was lines 6860-7024 pre-Plan-37-06).
//
// Scope: Recommend Tend modal — app pitch for sharing with your network
// (burger menu). UAT2-GAP-06 — exported and tested. Pre-renders 5 platform
// share-card images via the sibling share-canvas helper.

import { useState, useEffect } from 'react';
import { Leaf, Share2, Copy } from 'lucide-react';
import { renderRecommendCardForPlatform } from './share-canvas.js';
import { logEvent } from '../data/events.js';
import { logAffiliateClick, FEATURE_SOURCES } from '../data/affiliates.js';
import { CURRENT_MONTH } from '../constants.js';

export function RecommendModal({ onClose }) {
  const [copyConfirm, setCopyConfirm] = useState(false);
  const [channelFiles, setChannelFiles] = useState({});
  const shareUrl = window.location.origin + '/?utm_source=recommend';
  const encodedUrl = encodeURIComponent(shareUrl);

  // Per-channel copy variants — UK English, distinct from AchievementModal's copyFor.
  // Strings locked per CONTEXT.md — do not paraphrase.
  const copyForRecommend = {
    x:         `Been using Tend for my garden — it shows the right job at the right time each month. Give it a look 🌱 #gardening`,
    facebook:  `Tend is a lovely little app that tells me exactly what my garden needs each month — pruning, feeding, watering, the lot. Worth a try if you grow anything.`,
    instagram: `Tend keeps my garden on track 🌿 It tells you what to do each month — no more guessing. ${shareUrl}`,
    linkedin:  `I've been using Tend — a personalised garden planner that shows each month's care tasks tailored to your specific plants. A thoughtful bit of product design for anyone who gardens.`,
    whatsapp:  `Tend is great for staying on top of garden jobs — knows exactly what to do each month: ${shareUrl}`,
  };

  // logRecommend writes share-recommend-{channel} — discriminator for Phase 32 analytics.
  const logRecommend = (channel) => {
    logEvent('share_tapped', { type: 'recommend', channel });
    logAffiliateClick({ linkId: `share-recommend-${channel}`, feature: FEATURE_SOURCES.HOME, month: CURRENT_MONTH });
  };

  // Pre-render 5 platform Files in parallel on mount.
  // The cancelled flag prevents setChannelFiles being called after unmount.
  useEffect(() => {
    let cancelled = false;
    const channelIds = ['x', 'facebook', 'instagram', 'linkedin', 'whatsapp'];
    Promise.all(channelIds.map(ch =>
      renderRecommendCardForPlatform(ch).then(canvas => new Promise(resolve => {
        canvas.toBlob(blob => {
          if (!blob) { resolve([ch, null]); return; }
          const filename = `tend-recommend-${ch}.png`;
          resolve([ch, new File([blob], filename, { type: 'image/png' })]);
        }, 'image/png');
      }))
    )).then(entries => {
      if (cancelled) return;
      setChannelFiles(Object.fromEntries(entries.filter(([, f]) => f)));
    });
    return () => { cancelled = true; };
  }, []);

  // Per-channel click handler: prefer native-share with per-platform File on mobile;
  // silently fall through to window.open intent URL on desktop or navigator.share rejection.
  const handleChannelClick = async (channel, url) => {
    logRecommend(channel);
    const file = channelFiles[channel];
    if (file && typeof navigator !== 'undefined' && navigator.canShare && navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({
          title: 'Tend — personalised garden planner',
          text: copyForRecommend[channel],
          url: shareUrl,
          files: [file],
        });
        return;
      } catch (_) {
        // User cancel, permission denied, or platform rejection — silent fallthrough.
      }
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleCopy = async () => {
    logRecommend('copy');
    try { await navigator.clipboard.writeText(shareUrl); setCopyConfirm(true); setTimeout(() => setCopyConfirm(false), 2500); } catch (_) {}
  };

  const handleNativeShare = async () => {
    logRecommend('native');
    try { await navigator.share({ title: 'Tend — garden planner', text: copyForRecommend.x, url: shareUrl }); } catch (_) {}
  };

  const canNativeShare = typeof navigator !== 'undefined' && navigator.share;

  const channels = [
    { id: 'whatsapp', label: 'WhatsApp', url: `https://wa.me/?text=${encodeURIComponent(copyForRecommend.whatsapp)}` },
    { id: 'facebook', label: 'Facebook', url: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}` },
    { id: 'x', label: 'X', url: `https://x.com/intent/tweet?text=${encodeURIComponent(copyForRecommend.x)}&url=${encodedUrl}` },
    { id: 'linkedin', label: 'LinkedIn', url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}` },
  ];

  const chStyle = {
    display:"flex",alignItems:"center",justifyContent:"center",gap:6,
    flex:"1 1 45%",padding:"10px 0",background:"var(--c-surface)",color:"var(--c-heading)",
    border:"1px solid var(--c-border)",borderRadius:12,fontSize:'var(--font-small)',fontWeight:600,
    cursor:"pointer",textDecoration:"none",minWidth:0,
  };

  return (
    <div style={{position:"fixed",inset:0,zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center"}} onClick={onClose}>
      <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)"}}/>
      <div onClick={e => e.stopPropagation()} style={{
        position:"relative",zIndex:1001,background:"var(--c-bg)",borderRadius:18,padding:24,
        maxWidth:400,width:"calc(100% - 32px)",maxHeight:"90vh",overflowY:"auto",
      }}>
        {/* Test probes — hidden elements exposing channelFiles state for Vitest assertions */}
        <span data-testid="recommend-channel-files-count" className="hidden">{Object.keys(channelFiles).length}</span>
        {channelFiles.x && <span data-testid="recommend-channel-file-x-name" className="hidden">{channelFiles.x.name}</span>}
        {channelFiles.x && <span data-testid="recommend-channel-file-x-type" className="hidden">{channelFiles.x.type}</span>}

        <h2 style={{fontSize:"var(--font-h2)",fontWeight:600,color:"var(--c-heading)",margin:"0 0 8px",fontFamily:"var(--font-family-display)"}}>Recommend Tend</h2>

        {/* App pitch */}
        <div style={{background:"var(--c-surface)",borderRadius:12,border:"1px solid var(--c-border)",padding:16,marginBottom:16}}>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:10}}>
            <div style={{width:40,height:40,borderRadius:10,background:"var(--c-brand-deep)",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
              <Leaf size={20} style={{color:"var(--c-nav-text)"}}/>
            </div>
            <div>
              <div style={{fontSize:16,fontWeight:700,color:"var(--c-heading)",fontFamily:"var(--font-family-display)"}}>Tend</div>
              <div style={{fontSize:'var(--font-tiny)',color:"var(--c-text-3)"}}>Personalised garden planner</div>
            </div>
          </div>
          <p style={{fontSize:"var(--font-body)",color:"var(--c-text-2)",margin:0,lineHeight:1.6}}>
            Open the app, know exactly what to do in your garden this month. Tend creates a personalised care schedule for your plants — pruning, feeding, watering, and more — so nothing gets missed.
          </p>
        </div>

        {/* Primary: native share (mobile) */}
        {canNativeShare && (
          <button onClick={handleNativeShare} style={{
            display:"flex",alignItems:"center",justifyContent:"center",gap:8,
            width:"100%",padding:"12px 0",background:"var(--c-nav-bg)",color:"#fff",
            border:"none",borderRadius:20,fontSize:'var(--font-body)',fontWeight:600,cursor:"pointer",marginBottom:10,
          }}>
            <Share2 size={16}/> Share with a friend
          </button>
        )}

        {/* Channel buttons — each dispatches through handleChannelClick */}
        <p style={{fontSize:'var(--font-tiny)',color:"var(--c-text-3)",margin:"0 0 8px",fontWeight:600}}>Share a link via</p>
        <div style={{display:"flex",flexWrap:"wrap",gap:8,marginBottom:8}}>
          {channels.map(ch => (
            <button key={ch.id} data-testid={`recommend-channel-btn-${ch.id}`}
              onClick={(e) => { e.preventDefault(); handleChannelClick(ch.id, ch.url); }}
              style={chStyle}>
              {ch.label}
            </button>
          ))}
        </div>

        {/* Copy link */}
        <button onClick={handleCopy} style={{
          display:"flex",alignItems:"center",justifyContent:"center",gap:6,
          width:"100%",padding:"10px 0",background:"var(--c-surface)",color:"var(--c-heading)",
          border:"1px solid var(--c-border)",borderRadius:12,fontSize:'var(--font-small)',fontWeight:600,cursor:"pointer",marginBottom:8,
        }}>
          <Copy size={14}/> Copy link
        </button>
        {copyConfirm && (
          <p style={{fontSize:'var(--font-small)',color:"var(--c-success)",textAlign:"center",margin:"0 0 8px"}}>Link copied to clipboard</p>
        )}

        <button onClick={onClose} style={{
          width:"100%",padding:"10px 0",background:"var(--c-border)",color:"var(--c-text-2)",
          border:"none",borderRadius:20,fontSize:'var(--font-body)',fontWeight:600,cursor:"pointer",
        }}>Close</button>
      </div>
    </div>
  );
}
